import React from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiBookOpen } from 'react-icons/fi';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--background-overlay);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled(motion.div)`
  background-color: var(--background-primary);
  border-radius: 0;
  padding: 2rem;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h2`
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: 1.5rem;
  font-weight: 500;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0;
  transition: all 0.2s ease;
  
  &:hover {
    color: var(--text-primary);
    background-color: var(--background-secondary);
  }
`;

const CourseList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-height: 300px;
  overflow-y: auto;
`;

const CourseItem = styled.button`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  background-color: var(--background-primary);
  color: var(--text-primary);
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  width: 100%;
  
  &:hover {
    border-color: var(--primary-color);
    background-color: var(--background-secondary);
  }
`;

const CourseIcon = styled.div`
  color: var(--primary-color);
  display: flex;
  align-items: center;
`;

const CourseInfo = styled.div`
  flex: 1;
`;

const CourseName = styled.div`
  font-weight: 500;
  font-size: 1rem;
  margin-bottom: 0.25rem;
`;

const CourseCode = styled.div`
  font-size: 0.875rem;
  color: var(--text-secondary);
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  color: var(--text-secondary);
`;

function CourseSelectionModal({ isOpen, onClose, courses = [], electives = [], onCourseSelected, onElectiveSelected }) {
  const handleCourseSelect = (course) => {
    onCourseSelected(course);
  };

  const handleElectiveSelect = (elective) => {
    if (onElectiveSelected) {
      onElectiveSelected(elective);
    }
  };

  const hasCourses = courses && courses.length > 0;
  const hasElectives = electives && electives.length > 0;

  return (
    <AnimatePresence>
      {isOpen && (
        <ModalOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <ModalContent
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <ModalHeader>
              <ModalTitle>
                <FiBookOpen size={24} style={{ color: 'var(--primary-color)' }} />
                Select Course or Elective
              </ModalTitle>
              <CloseButton onClick={onClose}>
                <FiX size={20} />
              </CloseButton>
            </ModalHeader>

            {!hasCourses && !hasElectives ? (
              <EmptyState>
                <p>No courses or electives available</p>
              </EmptyState>
            ) : (
              <>
                {hasCourses && (
                  <>
                    <h3 style={{ marginTop: 0, marginBottom: '0.75rem', color: 'var(--text-primary)', fontSize: '1rem', fontWeight: 600 }}>Courses</h3>
                    <CourseList>
                      {courses.map((course) => (
                        <CourseItem
                          key={course.id}
                          onClick={() => handleCourseSelect(course)}
                        >
                          <CourseIcon>
                            <FiBookOpen size={20} />
                          </CourseIcon>
                          <CourseInfo>
                            <CourseName>{course.name}</CourseName>
                            <CourseCode>{course.code}</CourseCode>
                          </CourseInfo>
                        </CourseItem>
                      ))}
                    </CourseList>
                  </>
                )}
                {hasElectives && (
                  <>
                    <h3 style={{ marginTop: hasCourses ? '1.5rem' : 0, marginBottom: '0.75rem', color: 'var(--text-primary)', fontSize: '1rem', fontWeight: 600 }}>Electives</h3>
                    <CourseList>
                      {electives.map((elective) => (
                        <CourseItem
                          key={elective.id}
                          onClick={() => handleElectiveSelect(elective)}
                        >
                          <CourseIcon>
                            <FiBookOpen size={20} />
                          </CourseIcon>
                          <CourseInfo>
                            <CourseName>{elective.name}</CourseName>
                            <CourseCode>{elective.code} (Elective)</CourseCode>
                          </CourseInfo>
                        </CourseItem>
                      ))}
                    </CourseList>
                  </>
                )}
              </>
            )}
          </ModalContent>
        </ModalOverlay>
      )}
    </AnimatePresence>
  );
}

export default CourseSelectionModal;
